import { Experience } from '@mlbtv-clients/services';
import { ROUTES } from 'constants/screens';
import { MLBExperienceStorage } from 'experience/MLB';
import { Bookmark } from 'store/bookmarks';
import { ConfigEnv } from 'store/config';
import { getEnvFromHost } from 'utils/env';

import {
  BOOKMARKS_KEY,
  CLOSED_CAPTIONS_KEY,
  CONFIG_ENV_KEY,
  DEV_SETTINGS_KEY,
  EPG_CACHE_BUST_KEY,
  ERROR_BOUNDARY_RELOAD_HISTORY_KEY,
  EXPERIENCE,
  FAVORITE_TEAM_KEY,
  FOLLOWING_TEAMS_KEY,
  GETTING_STARTED_VIEWED_KEY,
  HIDE_SPOILERS_KEY,
  HDR_KEY,
  IS_MUTED_KEY,
  LOG_IN_AFTER_CREATE_KEY,
  MLB_EXPERIENCE_KEY,
  OKTA_AUTH_STORAGE_KEY,
  ONBOARDING_REDIRECT_PAGE,
  SHOW_TITLE_SAFE_OVERLAY_KEY,
  TEXT_TO_SPEECH_ENABLED_KEY,
  TEXT_TO_SPEECH_STRATEGY_KEY,
} from './constants';
import { getItem, getSubItem, removeItem, setItem, setSubItem, StorageType } from './storage';
import { TTSStrategy } from 'utils/textToSpeech/types';

const { ScreenReader } = TTSStrategy;

// App Env
export const getConfigEnvStorage = () =>
  getSubItem<ConfigEnv>(DEV_SETTINGS_KEY, CONFIG_ENV_KEY, {
    defaultValue: getEnvFromHost(),
  });

export const setConfigEnvStorage = (configEnv: ConfigEnv) =>
  setSubItem(DEV_SETTINGS_KEY, CONFIG_ENV_KEY, configEnv);

// Bookmarks
export const getBookmarksStorage = () => getItem<Bookmark[]>(BOOKMARKS_KEY, { defaultValue: [] });

export const setBookmarksStorage = (bookmarks: Bookmark[]) => setItem(BOOKMARKS_KEY, bookmarks);

// Cache Bust
export const getCacheBustStorage = () =>
  getItem<boolean>(EPG_CACHE_BUST_KEY, { defaultValue: false });

export const setCacheBustStorage = () => setItem(EPG_CACHE_BUST_KEY, true);

export const removeBustCacheStorage = () => removeItem(EPG_CACHE_BUST_KEY);

// Closed Captions
export const getClosedCaptionsStorage = () =>
  getItem<boolean>(CLOSED_CAPTIONS_KEY, { defaultValue: false });

export const setClosedCaptionsStorage = (captionsOn: boolean) =>
  setItem<boolean>(CLOSED_CAPTIONS_KEY, captionsOn);

// Dev Experience
export const getDevExperienceStorage = () =>
  getSubItem<Experience | null>(DEV_SETTINGS_KEY, EXPERIENCE, {
    defaultValue: null,
  });

export const setDevExperienceStorage = (experience: Experience | null) => {
  setSubItem(DEV_SETTINGS_KEY, EXPERIENCE, experience);
};

export const getMLBExperienceStorage = () =>
  getItem<MLBExperienceStorage>(MLB_EXPERIENCE_KEY, {
    defaultValue: {
      deviceId: '',
    },
  });

export const setMLBExperienceStorage = (experienceInfo: MLBExperienceStorage) =>
  setItem<MLBExperienceStorage>(MLB_EXPERIENCE_KEY, experienceInfo);

// Error Boundary
export const getErrorBoundaryReloadHistoryStorage = () =>
  getItem<number[]>(ERROR_BOUNDARY_RELOAD_HISTORY_KEY, {
    defaultValue: [],
    type: StorageType.sessionStorage,
  });

export const setErrorBoundaryReloadHistoryStorage = (history: number[]) =>
  setItem(ERROR_BOUNDARY_RELOAD_HISTORY_KEY, history, { type: StorageType.sessionStorage });

// HDR
export const getHDRStorage = () => getItem<boolean>(HDR_KEY, { defaultValue: true });

export const setHDRStorage = (captionsOn: boolean) => setItem<boolean>(HDR_KEY, captionsOn);

// Favorite Team
export const getFavoriteTeamStorage = () => getItem<number>(FAVORITE_TEAM_KEY, { defaultValue: 0 });

export const setFavoriteTeamStorage = (favoriteTeam: number) => {
  setItem(FAVORITE_TEAM_KEY, favoriteTeam);
};

// Following Teams
export const getFollowingTeamsStorage = () =>
  getItem<number[]>(FOLLOWING_TEAMS_KEY, { defaultValue: [] });

export const setFollowingTeamsStorage = (followingTeams: number[]) => {
  setItem(FOLLOWING_TEAMS_KEY, followingTeams);
};

// Getting Started Viewed
export const getGettingStartedViewed = () =>
  getItem<boolean>(GETTING_STARTED_VIEWED_KEY, { defaultValue: false });

export const setGettingStartedViewed = (gettingStartedViewed: boolean) => {
  setItem(GETTING_STARTED_VIEWED_KEY, gettingStartedViewed);
};

// Hide Spoilers
export const getHideSpoilersStorage = () =>
  getItem<boolean>(HIDE_SPOILERS_KEY, { defaultValue: false });

export const setHideSpoilersStorage = (hideSpoilers: boolean) => {
  setItem(HIDE_SPOILERS_KEY, hideSpoilers);
};

// IsMuted For Dev
export const getIsMutedStorage = () => getItem<boolean>(IS_MUTED_KEY, { defaultValue: false });

export const setIsMutedStorage = (captionsOn: boolean) =>
  setItem<boolean>(IS_MUTED_KEY, captionsOn);

// Log in after create
export const getLogInAfterCreateStorage = () =>
  getSubItem<boolean>(DEV_SETTINGS_KEY, LOG_IN_AFTER_CREATE_KEY, {
    defaultValue: false,
  });

export const setLogInAfterCreateStorage = (logInAfterCreate: boolean) => {
  setSubItem(DEV_SETTINGS_KEY, LOG_IN_AFTER_CREATE_KEY, logInAfterCreate);
};

// Redirect page after onboarding flow
export const getOnboardingRedirectPage = () =>
  getItem(ONBOARDING_REDIRECT_PAGE, {
    defaultValue: ROUTES.HOME,
    type: StorageType.sessionStorage,
  });

// Okta Auth
export const getOktaAuthStorage = () => {
  const item = getItem(OKTA_AUTH_STORAGE_KEY, {
    defaultValue: { accessToken: { accessToken: '' } },
  });
  return { accessToken: item.accessToken?.accessToken };
};

export const setOnboardingRedirectPage = (page: string) =>
  setItem(ONBOARDING_REDIRECT_PAGE, page, { type: StorageType.sessionStorage });

// Title Safe Overlay
export const getShowTitleSafeOverlayStorage = () =>
  getSubItem<boolean>(DEV_SETTINGS_KEY, SHOW_TITLE_SAFE_OVERLAY_KEY, {
    defaultValue: false,
  });

export const setShowTitleSafeOverlayStorage = (showOverlay: boolean) => {
  setSubItem(DEV_SETTINGS_KEY, SHOW_TITLE_SAFE_OVERLAY_KEY, showOverlay);
};

// Text to Speech
export const getTextToSpeechEnabledStorage = () =>
  getSubItem<boolean>(DEV_SETTINGS_KEY, TEXT_TO_SPEECH_ENABLED_KEY, {
    defaultValue: false,
  });

export const setTextToSpeechEnabledStorage = (textToSpeechEnabled: boolean) => {
  setSubItem(DEV_SETTINGS_KEY, TEXT_TO_SPEECH_ENABLED_KEY, textToSpeechEnabled);
};

export const getTextToSpeechStrategyStorage = () =>
  getSubItem<TTSStrategy>(DEV_SETTINGS_KEY, TEXT_TO_SPEECH_STRATEGY_KEY, {
    defaultValue: ScreenReader,
  });

export const setTextToSpeechStrategyStorage = (textToSpeechStrategy: TTSStrategy) => {
  setSubItem(DEV_SETTINGS_KEY, TEXT_TO_SPEECH_STRATEGY_KEY, textToSpeechStrategy);
};
